'use client'

import { useState } from 'react'
import { X } from 'lucide-react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { updateUser, updateUserImage } from '@/app/actions/user'
import { Upload } from '@/blocks/Form/Upload'
import { Media } from '@/components/Media'
import { Button } from '@/components/ui/button'
import { User } from '@/payload-types'

import { ProfileSteps } from './ProfileStep'

import type { Media as MediaType } from '@/payload-types'
import type { Control, FieldValues } from 'react-hook-form'

type ImageFormValues = {
  image: File | number | null
}

export const ImageForm = ({
  user,
  setOpenDrawer,
  setCurrentProfileStep: _setCurrentProfileStep,
  onUserUpdated,
}: {
  user: User
  setOpenDrawer: (open: boolean) => void
  setCurrentProfileStep: (step: ProfileSteps) => void
  onUserUpdated?: (updatedUser?: User) => Promise<void>
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const existingImageId =
    typeof user.image === 'object' && user.image != null
      ? (user.image as { id: number }).id
      : ((user.image as number | null) ?? null)

  const methods = useForm<ImageFormValues>({
    defaultValues: { image: existingImageId },
  })

  const currentImageValue = useWatch({ control: methods.control, name: 'image' })
  const showCurrentImage =
    existingImageId != null &&
    typeof user.image === 'object' &&
    user.image != null &&
    currentImageValue === existingImageId

  const clearImageSelection = () => {
    methods.setValue('image', null)
  }

  const onSubmit = async (data: ImageFormValues) => {
    setSubmitError(null)
    setIsLoading(true)

    try {
      const form = document.getElementById('image-form') as HTMLFormElement
      const formData = new FormData(form)
      formData.set('id', String(user.id))

      const imageValue = data.image
      if (imageValue instanceof File) {
        formData.set('image', imageValue)
      } else if (typeof imageValue === 'number') {
        const res = await updateUser({
          ...user,
          image: imageValue,
        })

        if (res.status === 'error') {
          throw new Error(res.message)
        }

        const updatedUser = res.data

        await onUserUpdated?.(updatedUser)
        setOpenDrawer(false)
        toast.success('Profile image updated')
        setIsLoading(false)
        return
      } else {
        const res = await updateUser({
          ...user,
          image: null,
        })

        if (res.status === 'error') {
          throw new Error(res.message)
        }

        const updatedUser = res.data

        await onUserUpdated?.(updatedUser)
        setOpenDrawer(false)
        toast.success('Profile image removed')
        setIsLoading(false)
        return
      }

      const res = await updateUserImage(formData)

      if (res.status === 'error') {
        setSubmitError(res.message)
        return
      }

      const updatedUser = res.data

      setOpenDrawer(false)
      await onUserUpdated?.(updatedUser)
      toast.success('Profile image updated successfully')
      // const updatedUser = (result as { user?: User }).user
      const newImageId =
        updatedUser?.image != null && typeof updatedUser.image === 'object'
          ? (updatedUser.image as { id: number }).id
          : ((updatedUser?.image as number | null) ?? null)
      methods.reset({ image: newImageId })
    } catch (error: unknown) {
      setSubmitError(error instanceof Error ? error.message : 'An error occurred while uploading')
      toast.error(error instanceof Error ? error.message : 'Upload failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form id="image-form" onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="m-auto my-6 max-w-xl">
          <h2 className="font-family-body text-2xl font-semibold text-foreground">Profile Image</h2>
          <p className="mt-3 mb-6 font-body text-card-foreground">
            Upload a profile photo. JPEG, PNG, or WEBP up to 15 MB.
          </p>
          {showCurrentImage && (
            <div className="mb-6 flex justify-center">
              <div className="relative inline-block">
                <Media
                  resource={user.image as MediaType}
                  context={'thumbnail'}
                  alt="Profile image"
                  width={100}
                  height={100}
                  imgClassName="rounded-full object-cover object-center"
                />
                <Button
                  type="button"
                  variant="icon"
                  aria-label="Clear image (save to confirm removal)"
                  disabled={isLoading}
                  onClick={clearImageSelection}
                  className="absolute -top-1 -right-1 size-6 rounded-full bg-destructive p-0 text-destructive-foreground shadow-md hover:bg-destructive/90"
                >
                  <X className="size-3.5 stroke-2" />
                </Button>
              </div>
            </div>
          )}
          <div className="mb-6">
            <Upload
              name="image"
              label="Profile photo"
              errors={methods.formState.errors}
              control={methods.control as unknown as Control<FieldValues>}
              required={false}
              uploadOnSubmit={true}
            />
          </div>

          <div className="flex flex-col gap-3">
            <Button
              type="submit"
              disabled={isLoading}
              className="mx-auto block w-full"
              labelClassName="w-fit mx-auto"
            >
              {isLoading ? 'Loading…' : 'Save'}
            </Button>
            {submitError && (
              <p className="m-auto mt-4 rounded-md bg-red-50 p-3 text-center text-sm text-red-500">
                {submitError}
              </p>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
